import { supabase } from '../config/supabase';
import { Product } from '../types';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'low_stock' | 'sale' | 'cash_register' | 'other';
  read: boolean;
  created_at: string;
}

export const notificationService = {
  async getNotifications() {
    // Obter usuário autenticado
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('Usuário não autenticado');
      return [];
    }

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) throw error;
    return data;
  },

  async markAsRead(notificationId: string) {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) throw error;
  },

  async createLowStockNotification(product: Product) {
    // Obter usuário autenticado
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('Usuário não autenticado');
      return;
    }

    const { error } = await supabase
      .from('notifications')
      .insert({
        type: 'low_stock',
        title: 'Estoque Baixo',
        message: `O produto "${product.name}" está com estoque baixo (${product.stock_quantity} unidades)`,
        read: false,
        user_id: user.id,
        created_at: new Date().toISOString()
      });

    if (error) throw error;
  },

  async createSaleNotification(saleNumber: string, total: number) {
    // Obter usuário autenticado
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('Usuário não autenticado');
      return;
    }

    const { error } = await supabase
      .from('notifications')
      .insert({
        type: 'sale',
        title: 'Nova Venda',
        message: `Venda #${saleNumber} foi finalizada no valor de R$ ${total.toFixed(2)}`,
        read: false,
        user_id: user.id,
        created_at: new Date().toISOString()
      });

    if (error) throw error;
  },

  async createCashRegisterOpenNotification(openingAmount: number, openingTime: string) {
    // Obter usuário autenticado
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('Usuário não autenticado');
      return;
    }

    const time = new Date(openingTime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const payload = {
      type: 'other' as const,
      title: 'Caixa Aberto',
      message: `Caixa foi aberto às ${time} com valor inicial de R$ ${openingAmount.toFixed(2)}`,
      read: false,
      user_id: user.id,
      created_at: new Date().toISOString()
    };
    
    console.log('📤 Enviando notificação de abertura:', payload);
    
    const { data, error } = await supabase
      .from('notifications')
      .insert(payload)
      .select();

    if (error) {
      console.error('❌ Erro detalhado ao criar notificação de abertura:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        payload
      });
      throw error;
    }
    console.log('✅ Notificação de abertura criada:', data);
    return data;
  },

  async createCashRegisterCloseNotification(closingAmount: number, difference: number, openingTime: string, closingTime: string) {
    // Obter usuário autenticado
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('Usuário não autenticado');
      return;
    }

    const openTime = new Date(openingTime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const closeTime = new Date(closingTime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const diffText = difference === 0 
      ? 'sem diferenças' 
      : difference > 0 
        ? `com sobra de R$ ${Math.abs(difference).toFixed(2)}` 
        : `com falta de R$ ${Math.abs(difference).toFixed(2)}`;
    
    const payload = {
      type: 'other' as const,
      title: 'Caixa Fechado',
      message: `Caixa foi fechado às ${closeTime} (aberto às ${openTime}) com valor de R$ ${closingAmount.toFixed(2)} - ${diffText}`,
      read: false,
      user_id: user.id,
      created_at: new Date().toISOString()
    };
    
    console.log('📤 Enviando notificação de fechamento:', payload);
    
    const { data, error } = await supabase
      .from('notifications')
      .insert(payload)
      .select();

    if (error) {
      console.error('❌ Erro detalhado ao criar notificação de fechamento:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        payload
      });
      throw error;
    }
    console.log('✅ Notificação de fechamento criada:', data);
    return data;
  }
};
